// Radar position templates — ported from soccer-frontend's players/radar/templates.ts
// (READ-ONLY parity source). Each template = a position group + a fixed list of
// per-90 metrics, and each metric carries hardcoded min/max BOUNDS (empirical
// population ranges — roughly the low/high end of that stat for that position).
// The radar scales a player's value between those bounds; there is no live
// distribution query. Some bounds are INVERTED (min > max) where a lower value is
// better (e.g. turnovers) — the scaling handles that automatically.
//
// The 6 POSITION templates have non-empty `positions`, so they can be
// auto-selected from a player's minutes. The 7th, "passing", is SKILL-based
// (positions: []) — it never auto-selects and is rendered ONLY by the dedicated
// Passing Radar widget (which scopes it to the player's position group + adds a
// peer overlay). It is deliberately NOT offered in the Radar widget's dropdown:
// there it would run unfiltered (overall per-90) and show the same template with
// different numbers than the Passing Radar widget. Its 3 formerly-missing keys
// (avg_pass_difficulty, expected_pass_success_perc, psax_90) landed with v1.4.3.

export interface Bounds {
  min: number;
  max: number;
}

export interface TemplateMetric {
  key: string;
  bounds: Bounds;
}

export interface RadarTemplate {
  id: string;
  label: string;
  /** golden position CODES this template covers (e.g. "CF"); matches the
   *  position_id strings returned by /player-stats grouped by position. */
  positions: string[];
  metrics: TemplateMetric[];
}

export const RADAR_TEMPLATES: RadarTemplate[] = [
  {
    id: "striker",
    label: "Striker",
    positions: ["RCF", "CF", "LCF"],
    metrics: [
      { key: "np_xg_90", bounds: { min: 0.06, max: 0.76 } },
      { key: "np_shots_90", bounds: { min: 1.2, max: 3.8 } },
      { key: "obv_shot_90", bounds: { min: -0.15, max: 0.25 } },
      { key: "obv_pass_90", bounds: { min: -0.09, max: 0.06 } },
      { key: "op_xga_90", bounds: { min: 0, max: 0.25 } },
      { key: "padj_pressures_90", bounds: { min: 8, max: 32 } },
      { key: "aerials_won_90", bounds: { min: 0.15, max: 3.75 } },
      { key: "turnovers_90", bounds: { min: 6.5, max: 2 } },
      { key: "obv_dribble_carry_90", bounds: { min: -0.06, max: 0.18 } },
      { key: "touches_in_box_90", bounds: { min: 0, max: 15 } },
      { key: "np_xg_per_shot", bounds: { min: 0.05, max: 0.2 } },
    ],
  },
  {
    id: "attacking_midfielder",
    label: "Attacking Midfielder",
    positions: ["RW", "RAM", "CAM", "LAM", "LW"],
    metrics: [
      { key: "np_xg_90", bounds: { min: 0.04, max: 0.54 } },
      { key: "np_shots_90", bounds: { min: 0.4, max: 4 } },
      { key: "obv_shot_90", bounds: { min: -0.15, max: 0.25 } },
      { key: "padj_pressures_90", bounds: { min: 9, max: 34 } },
      { key: "obv_pass_90", bounds: { min: -0.06, max: 0.26 } },
      { key: "op_xga_90", bounds: { min: 0.0, max: 0.3 } },
      { key: "obv_dribble_carry_90", bounds: { min: -0.02, max: 0.2 } },
      { key: "fouls_won_90", bounds: { min: 0.04, max: 3 } },
      { key: "turnovers_90", bounds: { min: 6, max: 1.6 } },
      { key: "touches_in_box_90", bounds: { min: 0, max: 10 } },
      { key: "np_xg_per_shot", bounds: { min: 0.02, max: 0.2 } },
    ],
  },
  {
    id: "midfielder",
    label: "Midfielder",
    positions: ["RDM", "CDM", "LDM", "RM", "RCM", "CM", "LCM", "LM"],
    metrics: [
      { key: "obv_90", bounds: { min: -0.06, max: 0.4 } },
      { key: "deep_progressions_90", bounds: { min: 1, max: 7 } },
      { key: "obv_pass_90", bounds: { min: -0.06, max: 0.19 } },
      { key: "op_xga_90", bounds: { min: 0.0, max: 0.2 } },
      { key: "obv_dribble_carry_90", bounds: { min: 0.0, max: 0.16 } },
      { key: "fouls_won_90", bounds: { min: 0.2, max: 2.2 } },
      { key: "turnovers_90", bounds: { min: 3.5, max: 0.5 } },
      { key: "padj_pressures_90", bounds: { min: 7, max: 28 } },
      { key: "padj_tackles_and_interceptions_90", bounds: { min: 1, max: 7 } },
      { key: "challenge_perc", bounds: { min: 35, max: 85 } },
      { key: "obv_defensive_action_90", bounds: { min: -0.02, max: 0.13 } },
    ],
  },
  {
    id: "centre_back",
    label: "Centre Back",
    positions: ["RCB", "CB", "LCB"],
    metrics: [
      { key: "aerial_perc", bounds: { min: 30, max: 80 } },
      { key: "aerials_won_90", bounds: { min: 0.4, max: 3.4 } },
      { key: "obv_90", bounds: { min: -0.05, max: 0.3 } },
      { key: "obv_pass_90", bounds: { min: -0.01, max: 0.17 } },
      { key: "change_in_passing_perc", bounds: { min: -25, max: 0 } },
      { key: "obv_dribble_carry_90", bounds: { min: 0.015, max: 0.1 } },
      { key: "fouls_committed_90", bounds: { min: 1.6, max: 0 } },
      { key: "padj_clearances_90", bounds: { min: 1.5, max: 7 } },
      { key: "blocks_per_shot", bounds: { min: 0.02, max: 0.12 } },
      { key: "challenge_perc", bounds: { min: 50, max: 100 } },
      { key: "obv_defensive_action_90", bounds: { min: -0.07, max: 0.17 } },
    ],
  },
  {
    id: "full_back",
    label: "Full Back",
    positions: ["RB", "LB", "RWB", "LWB"],
    metrics: [
      { key: "padj_tackles_and_interceptions_90", bounds: { min: 1.5, max: 7.5 } },
      { key: "deep_progressions_90", bounds: { min: 1.5, max: 7.5 } },
      { key: "obv_pass_90", bounds: { min: 0, max: 0.3 } },
      { key: "op_xga_90", bounds: { min: 0.0, max: 0.16 } },
      { key: "obv_dribble_carry_90", bounds: { min: 0, max: 0.14 } },
      { key: "turnovers_90", bounds: { min: 3.4, max: 0.4 } },
      { key: "aerial_perc", bounds: { min: 20, max: 80 } },
      { key: "padj_pressures_90", bounds: { min: 6, max: 24 } },
      { key: "fouls_committed_90", bounds: { min: 1.76, max: 0.18 } },
      { key: "challenge_perc", bounds: { min: 50, max: 90 } },
      { key: "obv_defensive_action_90", bounds: { min: -0.1, max: 0.1 } },
    ],
  },
  {
    id: "goalkeeper",
    label: "Goalkeeper",
    positions: ["GK"],
    metrics: [
      { key: "obv_gk_90", bounds: { min: -0.6, max: 0.3 } },
      { key: "long_ball_perc", bounds: { min: 30, max: 60 } },
      { key: "pass_into_danger_perc", bounds: { min: 28, max: 4 } },
      { key: "claimable_claim_success_perc", bounds: { min: 50, max: 100 } },
      { key: "gk_average_x_aggressive_action", bounds: { min: 6.5, max: 13 } },
      { key: "np_save_perc", bounds: { min: 53, max: 83 } },
      { key: "np_xg_positioning_suppression_90", bounds: { min: -0.12, max: 0.28 } },
      { key: "np_xg_stopping_suppression_90", bounds: { min: -0.5, max: 0.35 } },
      { key: "np_xg_save_difficulty_faced_90", bounds: { min: 0.4, max: 2.3 } },
      { key: "np_xg_faced_90", bounds: { min: 0.5, max: 2 } },
    ],
  },
  {
    // Skill-themed (not position-based) — passing ability across volume,
    // difficulty appetite, execution vs the xPass model, progression, creation,
    // and composure under pressure. Bounds ported from old Catalyst's "passing"
    // template. The two *_perc axes are 0–1 fractions in the data, scaled ×100
    // by radar.ts (bounds are in %).
    id: "passing",
    label: "Passing",
    positions: [],
    metrics: [
      { key: "passes_90", bounds: { min: 20, max: 80 } },
      { key: "avg_pass_difficulty", bounds: { min: 0.12, max: 0.34 } },
      { key: "expected_pass_success_perc", bounds: { min: 66, max: 88 } },
      { key: "psax_90", bounds: { min: -1.5, max: 1.5 } },
      { key: "pressured_pass_success_perc", bounds: { min: 50, max: 85 } },
      { key: "obv_pass_90", bounds: { min: -0.06, max: 0.26 } },
      { key: "deep_progressions_90", bounds: { min: 1, max: 7.5 } },
      { key: "key_passes_90", bounds: { min: 0, max: 2.5 } },
    ],
  },
];
